import React from 'react'
import { connect } from 'react-redux'
import { apiRequest, dataForResourcePath } from './reducers/data'

/*
The `Resource` component is a Redux-connected component that takes a render prop as it's child.
Given a resource `path`, it calls the render prop with the currently cached data at that path,
(or the `placeholder` data if provided) and makes an API request to refresh it with the latest
data if necessary.

The `Resource` component also provides it's children with helper functions that they can call to
update or delete the resource. Calling these helper methods makes the appropriate API requests
and then updates the local cache.

If you pass true for the `collection` prop, the `Resource` component expects an array to be
available at the path provided and exposes functions that allow items to be created, deleted
and updated.

Examples:

```jsx

  <Resource path={['me']} placeholder={{ emailAddress: 'Loading...' }}>
    {(me, { onSave }) => (
      <div>{me.emailAddress}</div>
    )}
  </Resource>
```

```jsx

  <Resource collection path={['properties']} placeholder={[]}>
    {(properties, { onCreateItem, onUpdateItem, onDeleteItem}) => (
      ...
      ... (call onCreateItem to add an item, etc.)
      ...
    )}
  </Resource>
```

*/

// Collection data provider

function pathIsValid(path) {
  return path.findIndex((component) => component == null || component === false) === -1
}

class _Resource extends React.Component {
  componentDidMount() {
    const { parent, dispatch, path, result, placeholder } = this.props

    const needInitialLoad = !parent || result === placeholder

    if (needInitialLoad && pathIsValid(path)) {
      dispatch(apiRequest(path))
    }
  }

  onRefresh = () => {
    const { parent, dispatch, path } = this.props
    if (parent) {
      return parent.onRefresh()
    }
    if (pathIsValid(path)) {
      return dispatch(apiRequest(path))
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.path.join('/') !== this.props.path.join('/')) {
      this.onRefresh()
    }
  }
  // Collection mutations

  onCreateItem = async (newItem) => {
    const { dispatch, path } = this.props
    const result = await dispatch(apiRequest(path, 'POST', newItem))
    await this.onRefresh()
    return result
  }

  onUpdateItem = async (updatedItem) => {
    const { dispatch, path } = this.props
    const result = await dispatch(apiRequest([...path, updatedItem.id], 'PUT', updatedItem))
    await this.onRefresh()
    return result
  }

  onDeleteItem = async (deleteItem) => {
    const { dispatch, path } = this.props
    const result = await dispatch(apiRequest([...path, deleteItem.id], 'DELETE'))
    await this.onRefresh()
    return result
  }

  // Single item mutations

  onSave = async (updatedJSON) => {
    const { dispatch, path } = this.props
    return dispatch(apiRequest(path, 'PUT', updatedJSON))
  }

  onDelete = async () => {
    const { dispatch, path } = this.props
    return dispatch(apiRequest(path, 'DELETE', {}))
  }

  render() {
    const { children, result, path, collection, dispatch } = this.props

    return children(result, {
      dispatch: dispatch,
      onCreateItem: collection && this.onCreateItem,
      onUpdateItem: collection && this.onUpdateItem,
      onDeleteItem: collection && this.onDeleteItem,
      onSave: !collection && this.onSave,
      onDelete: !collection && this.onDelete,
      onRefresh: this.onRefresh,
      NestedResource: (props) => (
        <Resource {...props} path={[...path].concat(props.path)} parent={this} />
      ),
    })
  }
}

const Resource = connect((state, ownProps) => {
  const data = pathIsValid(ownProps.path) && dataForResourcePath(state.data, ownProps.path)

  return {
    result: data || ownProps.placeholder,
  }
})(_Resource)

export default Resource
